import CloseIcon from '@mui/icons-material/Close';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Stripe, { StripeError } from '@stripe/stripe-js';
import { useEffect, useState } from 'react';
import { useMixpanel } from 'react-mixpanel-browser';

import { useCustomerInfo } from '@/api/customers';
import { useUpdateStripeCustomerProfile } from '@/api/customers/updateStripeCustomerProfile';
import { useInvoicesList } from '@/api/invoices';
import { PaymentSessionResponse, PaymentSessionType, createPaymentSession } from '@/api/payments';
import { PaymentMethod, useSubscriptionsInfo } from '@/api/subscriptions';
import { queryClient } from '@/common/react-query';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY } from '@/config';
import { formatPaymentMethod, stripePromise } from '@/utils';

import { DisplayCurrency } from '../DisplayCurrency';

export const PaymentMethodDialog = ({
    type,
    onClose,
    onChangePaymentMethod,
    open,
}: {
    type: PaymentSessionType;
    onClose: () => void;
    onChangePaymentMethod?: (paymentMethod: PaymentMethod) => void;
    open: boolean;
}) => {
    const mixpanel = useMixpanel();
    const [isLoading, setLoading] = useState<boolean>();
    const [sessionSecrets, setSessionSecrets] = useState<PaymentSessionResponse>();
    const [error, setError] = useState<Error>();

    function handleClose() {
        // just in case they sneakily deleted their default and then escaped from the dialog without
        // giving us a new one (onSavedPaymentMethodRemove would be more reliable, but currently only
        // available to beta program)
        queryClient.invalidateQueries({ queryKey: [STRIPE_CURRENT_CUSTOMER_INFO_QUERY_KEY] });

        setSessionSecrets(undefined);
        onClose();
    }

    useEffect(() => {
        if (open && !isLoading && !error && !sessionSecrets) {
            mixpanel?.track('PaymentMethodDialog open', { type });
            setLoading(true);
            setError(undefined);
            createPaymentSession(type)
                .then(setSessionSecrets)
                .catch(setError)
                .finally(() => setLoading(false));
        }
    }, [isLoading, error, sessionSecrets, open, type, mixpanel]);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth={false}>
            <DialogTitle sx={{ m: 0, p: 2, minWidth: 'calc(min(100vw, 640px))' }}>Payment method</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            {sessionSecrets?.data?.client_secret ? (
                <Elements
                    stripe={stripePromise}
                    options={{
                        clientSecret: sessionSecrets.data.client_secret,
                        customerSessionClientSecret: sessionSecrets.data.customer_session_client_secret,
                    }}
                >
                    <PaymentDialogContent
                        type={type}
                        onChangePaymentMethod={onChangePaymentMethod}
                        onClose={handleClose}
                    />
                </Elements>
            ) : (
                <DialogContent>
                    {error ? <Typography color="error">{error.message}</Typography> : <GenericSkeleton lines={3} />}
                </DialogContent>
            )}
        </Dialog>
    );
};

const PaymentDialogContent = ({
    type,
    onChangePaymentMethod,
    onClose,
}: {
    type: PaymentSessionType;
    onChangePaymentMethod?: (paymentMethod: PaymentMethod) => void;
    onClose: () => void;
}) => {
    const mixpanel = useMixpanel();
    const { data: customerInfo } = useCustomerInfo();
    const stripe = useStripe();
    const elements = useElements();
    const [isConfirmationPending, setConfirmationPending] = useState(false);
    const [elementVisible, setElementVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>();
    const { mutateAsync: updateStripeCustomerProfile } = useUpdateStripeCustomerProfile();
    const { data: openInvoices } = useInvoicesList({
        status: 'open',
    });
    const { data: subscriptionInfo } = useSubscriptionsInfo();
    /*
        It is not strictly necessary to match the open invoice to the latest invoice on the subscription,
        but the backend will attempt to pay the exact invoice from the latest_invoice field of the subscription when a new payment method is added.
        So this is to be extra accurate in the messaging.
     */
    const overdueInvoiceAmount = openInvoices?.data?.find(
        (invoice) => invoice.id === subscriptionInfo?.data?.[0]?.latest_invoice_id
    )?.amount_due;
    const showOverdueInvoicePaymentAttemptMessage = overdueInvoiceAmount !== undefined && overdueInvoiceAmount > 0;

    const handleSubmit = async () => {
        if (!stripe || !elements) {
            return null;
        }

        setConfirmationPending(true);
        setErrorMessage(undefined);
        mixpanel?.track('PaymentMethodDialog submit', { type });
        let error: StripeError | Error | undefined = undefined;
        try {
            const result = await stripe.confirmSetup({
                elements,
                confirmParams: {
                    payment_method_data: {
                        billing_details: { email: customerInfo?.data?.email },
                        ...(type === 'subscription' && { allow_redisplay: 'always' }),
                    },
                    expand: ['payment_method'],
                },
                redirect: 'if_required',
            });
            error = result.error;
            if (result.setupIntent?.payment_method) {
                const formattedPaymentMethod = formatPaymentMethod(
                    result.setupIntent.payment_method as Stripe.PaymentMethod
                );
                mixpanel?.track('PaymentMethodDialog change complete', {
                    type,
                    methodType: formattedPaymentMethod.type,
                });

                onChangePaymentMethod?.(formattedPaymentMethod);
                if (type === 'subscription') {
                    await updateStripeCustomerProfile({
                        default_payment_method_id: formattedPaymentMethod.id,
                    });
                }
            }
        } catch (err) {
            error = err instanceof Error ? err : new Error(String(err));
        }

        setConfirmationPending(false);

        if (error) {
            mixpanel?.track('PaymentMethodDialog error', { type, error: error.message });
            setErrorMessage(error.message);
        } else {
            onClose();
        }
    };

    return (
        <>
            <DialogContent>
                {!elementVisible && <GenericSkeleton lines={3} />}
                <PaymentElement
                    options={{
                        readOnly: isConfirmationPending,
                        layout: {
                            type: 'accordion',
                            defaultCollapsed: false,
                            radios: 'if_multiple',
                        },
                        fields: { billingDetails: { email: 'never' } },
                        defaultValues: { billingDetails: { name: customerInfo?.data?.name } },
                    }}
                    onLoaderStart={() => setElementVisible(true)}
                />
                {errorMessage && <Typography color="error">{errorMessage}</Typography>}
                {showOverdueInvoicePaymentAttemptMessage && (
                    <Typography variant="body2" sx={{ mt: 2 }}>
                        We will automatically charge your overdue invoice of{' '}
                        <DisplayCurrency value={overdueInvoiceAmount} cents sx={{ display: 'inline-block' }} /> with
                        this new payment method.
                    </Typography>
                )}
            </DialogContent>
            {elementVisible && (
                <DialogActions>
                    <Button onClick={onClose} disabled={isConfirmationPending}>
                        Cancel
                    </Button>
                    <Button variant="contained" disabled={isConfirmationPending} autoFocus onClick={handleSubmit}>
                        Save
                    </Button>
                </DialogActions>
            )}
        </>
    );
};
